// Three Spirit navigation web components. Added by Oleg @ IAKOE.
import { DialogFocus, listen } from 'ia-drawer';

const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

if (!customElements.get('ts-nav-panel')) {
  class TSNavPanel extends HTMLElement {
    connectedCallback() {
      this.toggle = document.querySelector(`[aria-controls="${this.id}"]`);
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-haspopup', 'dialog');
      this.dialogFocus = new DialogFocus(this, { extra: [this.toggle] });

      this.listeners?.abort();
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      listen(this.toggle, 'click', () => this.onToggle(), signal);
      listen(document, 'keydown', (e) => this.onKeydown(e), signal);
      // Close drawer on any nav link click
      listen(this, 'click', (e) => {
        if (e.target.closest('a[href]')) this.close();
      }, signal);
      // The drawer exists on mobile only: crossing to desktop releases it.
      this.mq = window.matchMedia('(min-width: 1024px)');
      const onMq = () => { if (this.mq.matches && this.isOpen) this.close({ returnFocus: false }); };
      if (this.mq.addEventListener) listen(this.mq, 'change', onMq, signal);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.dialogFocus?.deactivate({ returnFocus: false });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    onToggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', 'Close navigation menu');
      this.toggle.querySelector('ia-hamburger')?.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      // Dialog semantics only while it is a drawer; on desktop the same element is the inline nav.
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-label', 'Navigation menu');
      this.dialogFocus.activate(this.toggle);
    }

    close({ returnFocus = true } = {}) {
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', 'Open navigation menu');
      this.toggle.querySelector('ia-hamburger')?.classList.remove('is-active');
      document.body.style.overflow = '';
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.removeAttribute('aria-label');
      this.dialogFocus.deactivate({ returnFocus });
    }

    onKeydown(e) {
      if (e.key === 'Escape' && this.isOpen) this.close();
    }
  }
  customElements.define('ts-nav-panel', TSNavPanel);
}

if (!customElements.get('ts-nav-l1')) {
  class TSNavL1 extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      if (!this.details) return;
      this.details.addEventListener('toggle', this.onToggle.bind(this));
      this._docClick = this.onDocClick.bind(this);
      this._keydown = this.onKeydown.bind(this);
      document.addEventListener('click', this._docClick);
      document.addEventListener('keydown', this._keydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._docClick);
      document.removeEventListener('keydown', this._keydown);
    }

    onToggle() {
      if (!isDesktop() || !this.details.open) return;
      // Desktop: close sibling dropdowns
      document.querySelectorAll('ts-nav-l1 > details[open]').forEach((d) => {
        if (d !== this.details) d.open = false;
      });
    }

    onDocClick(e) {
      if (isDesktop() && this.details.open && !this.contains(e.target)) {
        this.details.open = false;
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape' && isDesktop() && this.details.open) {
        this.details.open = false;
      }
    }
  }
  customElements.define('ts-nav-l1', TSNavL1);
}

if (!customElements.get('ts-nav-area')) {
  customElements.define('ts-nav-area', class extends HTMLElement {});
}
